import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { buildInfo, sentryRelease } from './shared/buildInfo';
import { env } from './shared/utils/env';
import './index.css';
import i18n from './i18n';
import App from './App.jsx';

console.log(
  `%c Zehn Docs %c v${buildInfo.version} `,
  'background: #4C78DF; color: #fff; border-radius: 3px 0 0 3px; padding: 2px 4px; font-weight: bold;',
  'background: #324E8C; color: #fff; border-radius: 0 3px 3px 0; padding: 2px 4px;'
);

// Initialize Sentry for error tracking. It reads the pod's runtime config
// (shared/utils/env.js): on whenever a DSN is set, tagged with this deploy's
// environment and release name.
const sentryDsn = env('VITE_SENTRY_DSN');

Sentry.init({
  dsn: sentryDsn,
  environment: env('VITE_SENTRY_ENVIRONMENT') || 'development',
  release: sentryRelease,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: parseFloat(env('VITE_SENTRY_TRACES_SAMPLE_RATE')) || 1.0,
  // Session Replay
  replaysSessionSampleRate: 0.1, // 10% of sessions
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
  enabled: !!sentryDsn,
});

import {setThemePalette} from "./shared/utils/colors.js";

// Paint the right colours on the very first frame.
//
// GlobalContext applies the palette from an effect, which runs after the first
// paint — so without this, that frame has no --background at all and shows
// through as whatever the browser defaults to. Office's THEME_CHANGE lands
// later still, being gated on IFRAME_READY plus a token fetch.
//
// ?theme= comes from office and is authoritative: this origin's localStorage is
// partitioned when we are framed, so its copy can be stale or absent. Writing
// it back means GlobalContext reads the same value when it mounts.
const urlTheme = new URLSearchParams(window.location.search).get("theme");
if (urlTheme === "light" || urlTheme === "dark") {
    localStorage.setItem("theme", urlTheme);
}
setThemePalette(urlTheme || localStorage.getItem("theme") || "light");

createRoot(document.getElementById('root')).render(
  // The fallback renders outside every React provider (and before the i18n
  // resources may have loaded), so it reads the i18n instance directly. The
  // function form defers t() to the moment an error is actually caught.
  <Sentry.ErrorBoundary fallback={() => <p>{i18n.t('common:errorFallback')}</p>}>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </Sentry.ErrorBoundary>
);
